'use client';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
   Box,
   Flex,
   Table,
   Tbody,
   Td,
   Text,
   Tr,
   useColorModeValue,
   useDisclosure,
   Checkbox,
   Drawer,
   DrawerOverlay,
   DrawerContent,
   DrawerCloseButton,
   DrawerHeader,
   DrawerBody,
   useOutsideClick,
   Spinner,
   InputGroup,
   Thead,
   Th,
   Grid,
   GridItem,
   Input,
   Button,
   Modal,
   ModalOverlay,
   ModalContent,
   ModalHeader,
   ModalFooter,
   ModalBody,
   ModalCloseButton,
   Image,
   IconButton,
   Tooltip,
   Popover,
   PopoverTrigger,
   PopoverContent,
   PopoverBody,
   useColorMode,
} from '@chakra-ui/react';
import Select from 'react-select';
import { LuRefreshCcw } from 'react-icons/lu';
import { ViewIcon, SearchIcon, ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { motion } from 'framer-motion';
import {
   createColumnHelper,
   flexRender,
   getCoreRowModel,
   getSortedRowModel,
   useReactTable,
} from '@tanstack/react-table';

// Components
import Card from 'components/card/Card';
import PeriodDatePicker from 'components/RangePicker/PeriodDatePicker';
import { renderDynamicDropdown } from 'components/ui/DynamicDropdown';
import { columnsReportsSalesDetail } from '../../customDataTable/variables/columnsData';
import SalesPurchaseFilter from '../components/SalesPurchaseFilter';
// Redux
import { useDispatch, useSelector } from 'react-redux';
import { getCompanies } from '../../../../redux/actions/company';
import { getReportPurchaseDetail } from '../../../../redux/actions/reports';

// Utils
import { getShareCompanyOptions } from 'utils/util';
import { REPORT_TYPE } from 'constants';

import SalesDetails from 'components/salesDetails/SalesDetails';
import { deletePurchaseInvoice, getPurchaseInvoice } from '../../../../redux/actions/purchaseInvoice';
import { deleteRecord } from 'utils/util';

// Assets
import nodata from 'assets/img/nfts/no data.svg';
import Download from '../../../../assets/img/nfts/download.svg';
import xls from '../../../../../src/assets/img/nfts/xls.svg';
import back from '../../../../../src/assets/img/nfts/back.svg';
import DotIcon from '../../../../assets/img/nfts/dot.svg';
import { useGlobal } from 'contexts';
import * as XLSX from 'xlsx-js-style';
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import autoTable from 'jspdf-autotable';
import { IoFilterOutline } from 'react-icons/io5';
import { useUser } from 'contexts';
import Loader from 'views/admin/default/Loader';
import DeletePasswordModal from 'views/admin/sales/Pages/SalesInvoiceMaster/DeletePasswordModal';
import { VariableSizeList } from 'react-window';

// Constants
const REPORT_TYPE_OPTIONS = [
   { value: 'date', label: 'Date' },
   { value: 'party', label: 'Party' },
   { value: 'broker', label: 'Broker' },
];
const SEARCH_DEBOUNCE_DELAY = 300;

const MotionFlex = motion(Flex);
const MotionSearchIcon = motion(SearchIcon);

// Helper Components
const ColumnVisibilityModal = ({ isOpen, onClose, columns, columnVisibility, setColumnVisibility }) => (
   <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
         <ModalHeader>Show/Hide Columns</ModalHeader>
         <ModalCloseButton />
         <ModalBody>
            {columns.map((column) => (
               <Flex key={column.id} mb={2} alignItems="center">
                  <Checkbox
                     isChecked={!!columnVisibility[column.id]}
                     onChange={(e) => {
                        setColumnVisibility((prev) => ({
                           ...prev,
                           [column.id]: e.target.checked,
                        }));
                     }}
                     mr={2}
                  />
                  <Text>{column.header}</Text>
               </Flex>
            ))}
         </ModalBody>
         <ModalFooter>
            <Button colorScheme="blue" onClick={onClose}>
               Close
            </Button>
         </ModalFooter>
      </ModalContent>
   </Modal>
);

const NoDataFound = ({ colSpan }) => (
   <Tr justifyContent="center" alignItems="center" width="100%" height="100%">
      <Td colSpan={colSpan}>
         <Grid justifyContent="center" alignItems="center" gap={9}>
            <img src={nodata} alt="No Data Found" width="200px" height="200px" />
            <GridItem justifySelf="center">
               <label>No purchase data found</label>
            </GridItem>
         </Grid>
      </Td>
   </Tr>
);

const PurchaseSummary = () => {
   // Consolidated state
   const [state, setState] = useState({
      companyFilter: [],
      reportType: 'date',
      searchTerm: '',
      expandedItems: {},
      sorting: [],
      columnVisibility: {},
      isColumnSelectorOpen: false,
      isLoading: false,
      expanded: false,
   });
   const [isModalOpen, setIsModalOpen] = useState(false);
   const [dataId, setDataId] = useState(null);
   const { colorMode } = useColorMode();
   const isDark = colorMode === 'dark';
   // const [companyFilter, setCompanyFilter] = useState([]);
   const listRef = useRef(null);
   const [containerHeight, setContainerHeight] = useState(700);
   const [flattenedData, setFlattenedData] = useState([]);
   const containerRef = useRef(null);
   const [isFilterLoading, setIsFilterLoading] = useState(false);
   const isFirstRenderFilter = useRef(true);

   // Refs
   const refs = {
      abortController: useRef(null),
      searchTimeout: useRef(null),
      wrapper: useRef(),
      fromDateInput: useRef(null),
   };

   const lastFetchParamsRef = useRef(null);
   const initialLoadRef = useRef(false);

   // Redux
   const dispatch = useDispatch();
   const company = useSelector((state) => state.company.list || []);
   const salesReport = useSelector((state) => state.reports?.purchaseDetail || []);
   const daybookData = useSelector((state) => state.daybook?.list || []);
   const dbName = company?.dbName;
   const { isOpen, onOpen, onClose } = useDisclosure();

   const viewData = useSelector((state) => state?.purchaseInvoice?.single);
   const { viewDetail, paginate } = useMemo(() => {
      const data = viewData;

      const pagination = viewData?.pagination;

      return { viewDetail: data, paginate: pagination };
   }, [viewData, viewData?.pagination]);
   const [isEditFormOpen, setIsEditFormOpen] = React.useState(false);
   const [isOpenPurchaseReturnSingleView, setIsOpenPurchaseReturnSingleView] = React.useState(null);

   const { isOpen: isFilterOpen, onOpen: onFilterOpen, onClose: onFilterClose } = useDisclosure();
   const companyIds = useMemo(
      () =>
         state.companyFilter.length > 0
            ? state.companyFilter
            : localStorage.getItem('companyId')
               ? [parseInt(localStorage.getItem('companyId'))]
               : [],
      [state.companyFilter],
   );
   const { user } = useUser();
   const admin = user?.isAdmin;
   const rights = user?.userRights?.find((ur) => ur?.companyId === user?.loginhistory?.companyId);
   const companyId = Number(localStorage.getItem('companyId'));
   const userRights = user?.user?.userRights || user?.userRights;
   const purchaseInvoiceRight = Array.isArray(userRights)
      ? userRights.find((right) => right.companyId === companyId)?.purchaseinvoice
      : null;
   const purchaseReturnRight = Array.isArray(userRights)
      ? userRights.find((right) => right.companyId === companyId)?.purchasereturn
      : null;

   // Theme values
   const theme = {
      textColor: useColorModeValue('secondaryGray.900', 'white'),
      backgroundColor: useColorModeValue('white', '#101F4A'),
      borderColor: useColorModeValue('gray.200', 'whiteAlpha.100'),
      bg: useColorModeValue('#f2f7f2', '#1a254b'),
      icon: useColorModeValue('#000000', '#ffffff'),
   };
   const controlBg = useColorModeValue('#FFFFFF', '#2D3748');
   const controlBorder = useColorModeValue('#E2E8F0', '#4A5568');
   const controlBorderHover = useColorModeValue('#CBD5E0', '#718096');
   const focusBoxShadow = useColorModeValue('0 0 0 1px #3182CE', '0 0 0 1px #63B3ED');
   const placeholderColor = useColorModeValue('#A0AEC0', '#718096');
   const optionBg = useColorModeValue('#FFFFFF', '#2D3748');
   const optionHoverBg = useColorModeValue('#EDF2F7', '#4A5568');
   const menuBg = useColorModeValue('#FFFFFF', '#2D3748');
   const menuBorder = useColorModeValue('#E2E8F0', '#4A5568');
   const indicatorColor = useColorModeValue('#718096', '#A0AEC0');
   const indicatorHoverColor = useColorModeValue('#4A5568', '#CBD5E0');
   const { companyList } = useGlobal();
   const { userGlobalSettingsData } = useGlobal();
   const [dateRange, setDateRange] = useState(() => {
      const today = new Date();
      const sevenDaysAgo = new Date();
      sevenDaysAgo.setDate(today.getDate() - 6);

      return {
         fromDate: sevenDaysAgo.toISOString().split('T')[0], // last 7th day
         toDate: today.toISOString().split('T')[0], // today
      };
   });

   // Memoized values
   const companyOptions = useMemo(() => {
      return getShareCompanyOptions({ dbName, company: companyList });
   }, [companyList, dbName]);

   const lastcompanyOptions = useMemo(() => {
      const currentUserCode = user?.loginhistory?.code;
      const currentCompanyId = user?.loginhistory?.companyId;

      // SHARED COMPANY MODE: When code is present
      if (currentUserCode) {
         // Find the company with matching ID AND matching dbName
         const sharedCompany = companyList.find(
            company => company.id === currentCompanyId && company.dbName === currentUserCode
         );

         if (sharedCompany) {
            return [{
               value: sharedCompany.id,
               label: sharedCompany.name,
               id: sharedCompany.id,
               name: sharedCompany.name,
            }];
         }

         // Fallback: try to find by dbName only
         const companiesWithDbName = companyList.filter(company => company.dbName === currentUserCode);
         if (companiesWithDbName.length > 0) {
            return companiesWithDbName.map(company => ({
               value: company.id,
               label: company.name,
               id: company.id,
               name: company.name,
            }));
         }

         return [];
      }

      // NORMAL MODE: When no code, show companies without dbName
      return companyList
         .filter(company => !company.dbName)
         .map(company => ({
            value: company.id,
            label: company.name,
            id: company.id,
            name: company.name,
         }));
   }, [companyList, user?.loginhistory?.code, user?.loginhistory?.companyId]);

   // const columns = useMemo(() => {
   //    const columnHelper = createColumnHelper();
   //    return columnsReportsSalesDetail.map((column) =>
   //       columnHelper.accessor(column.id, {
   //          id: column.id,
   //          header: () => (
   //             <Text
   //                justifyContent="space-between"
   //                align="center"
   //                fontSize={{ sm: '10px', lg: '12px' }}
   //                color={theme.textColor}>
   //                {column.header}
   //             </Text>
   //          ),
   //          cell: (info) => <Flex align="center">{info.getValue()}</Flex>,
   //       }),
   //    );
   // }, [theme.textColor]);

   const columns = useMemo(() => {
      const columnHelper = createColumnHelper();

      return columnsReportsSalesDetail.map((column) =>
         columnHelper.accessor(column.id, {
            id: column.id,
            header: () => {
               return (
                  <div
                     className={`
            flex justify-between items-center px-3 max-w-[150px]
            text-xs lg:text-sm 
            ${isDark ? 'text-gray-100' : 'text-gray90-0'}
            font-medium
          `}>
                     {column.header}
                  </div>
               );
            },
            cell: (info) => {
               const cellValue = info.getValue();
               const columnId = column.id;

               // Handle numeric columns with special formatting
               if (['netAmount', 'basicAmount', 'taxableAmount', 'gstAmount'].includes(columnId) && cellValue) {
                  return (
                     <div className={`flex items-center justify-end ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                        ₹{Number(cellValue).toLocaleString()}
                     </div>
                  );
               }

               // Handle date columns
               if (columnId.includes('Date') && cellValue) {
                  return (
                     <div className={`flex items-center ${isDark ? 'text-gray-100' : 'text-gray-900'}`}>
                        {new Date(cellValue).toLocaleDateString()}
                     </div>
                  );
               }

               // Handle numeric columns
               const isNumeric = [
                  'netAmount',
                  'dueDays',
                  'returnadjamount',
                  'otheradjamount',
                  'balanceamount',
                  'recDate',
                  'recBook',
                  'qty',
                  'quantity',
               ].includes(columnId);

               return (
                  <div
                     className={`
            flex items-center
            ${isNumeric ? 'justify-end' : 'justify-start'}
            ${isDark ? 'text-gray-100' : 'text-gray-900'}
          `}>
                     {cellValue}
                  </div>
               );
            },
         }),
      );
   }, [isDark]);

   // Table instance
   const table = useReactTable({
      data: salesReport,
      columns,
      state: {
         sorting: state.sorting,
         columnVisibility: state.columnVisibility,
      },
      onSortingChange: (sorting) => setState((prev) => ({ ...prev, sorting })),
      onColumnVisibilityChange: (visibility) => setState((prev) => ({ ...prev, columnVisibility: visibility })),
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
   });

   // Effects
   useEffect(() => {
      dispatch(getCompanies());
   }, [dispatch]);

   useEffect(() => {
      if (isFirstRenderFilter.current) {
         isFirstRenderFilter.current = false;
         return;
      }
      setIsFilterLoading(false);
   }, [salesReport])

   useEffect(() => {
      const initialColumnVisibility = {};
      columnsReportsSalesDetail.forEach((column) => {
         initialColumnVisibility[column.id] = true;
      });
      setState((prev) => ({ ...prev, columnVisibility: initialColumnVisibility }));
   }, []);

   useEffect(() => {
      const storedCompanyId = localStorage.getItem('companyId');
      if (storedCompanyId && companyList.length > 0) {
         const companyId = parseInt(storedCompanyId);
         const matchingCompany = companyList.find((comp) => comp.id === companyId);
         if (matchingCompany) {
            setState((prev) => ({ ...prev, companyFilter: [matchingCompany.id] }));
         }
      }
   }, [companyList]);

   // Data fetching
   const fetchData = useCallback(() => {
      if (dateRange.fromDate && dateRange.toDate && companyIds.length > 0) {
         // Prevent duplicate calls with same parameters
         const currentParams = JSON.stringify({
            companyIds: companyIds.join(','),
            type: state.reportType,
            fromDate: dateRange.fromDate,
            toDate: dateRange.toDate,
            search: state.searchTerm.trim(),
         });
         console.log('data', dateRange, companyIds);

         if (lastFetchParamsRef.current === currentParams) {
            return; // Skip if same parameters
         }

         lastFetchParamsRef.current = currentParams;
         setState((prev) => ({ ...prev, isLoading: true }));

         dispatch(
            getReportPurchaseDetail({
               companyIds: companyIds.join(','),
               type: state.reportType,
               fromDate: dateRange.fromDate,
               toDate: dateRange.toDate,
               search: state.searchTerm.trim(),
            }),
         ).finally(() => {
            setState((prev) => ({ ...prev, isLoading: false }));
         });
      }
   }, [companyIds, state.reportType, dateRange.fromDate, dateRange.toDate, state.searchTerm, dispatch]);

   useEffect(() => {
      // Skip initial mount if no company filter set yet
      if (!initialLoadRef.current && companyIds.length === 0) {
         return;
      }

      // Clear any existing timeout
      if (refs.searchTimeout.current) {
         clearTimeout(refs.searchTimeout.current);
      }

      // Set up debouncing for search, immediate for other changes
      const delay = state.searchTerm ? SEARCH_DEBOUNCE_DELAY : 0;

      refs.searchTimeout.current = setTimeout(() => {
         fetchData();
      }, delay);

      // Mark as loaded after first successful setup
      if (!initialLoadRef.current) {
         initialLoadRef.current = true;
      }

      return () => {
         if (refs.searchTimeout.current) {
            clearTimeout(refs.searchTimeout.current);
         }
      };
   }, [
      companyIds,
      state.reportType,
      dateRange.fromDate,
      dateRange.toDate,
      state.searchTerm,
      fetchData,
      refs.searchTimeout,
   ]);

   // Event handlers
   const handleCompanyChange = useCallback((selectedOptions) => {
      setState((prev) => ({ ...prev, companyFilter: selectedOptions ? selectedOptions : [] }));
   }, []);

   const handleDateRangeChange = useCallback((dates) => {
      if (!dates || dates.length !== 2) return;
      const [from, to] = dates.map((date) => (typeof date === 'string' ? date : date.toISOString().slice(0, 10)));
      console.log('dates data', from, to);
      setState((prev) => ({ ...prev, dateRange: { fromDate: from, toDate: to } }));
      setDateRange({ fromDate: from, toDate: to });
   }, []);

   const toggleExpand = useCallback((id) => {
      setState((prev) => ({
         ...prev,
         expandedItems: { ...prev.expandedItems, [id]: !prev.expandedItems[id] },
      }));
   }, []);

   const handleSearchChange = useCallback(
      (e) => {
         const value = e.target.value;
         setState((prev) => ({ ...prev, searchTerm: value }));

         if (refs.searchTimeout.current) {
            clearTimeout(refs.searchTimeout.current);
         }

         refs.searchTimeout.current = setTimeout(() => {
            fetchData(value);
         }, SEARCH_DEBOUNCE_DELAY);
      },
      [fetchData],
   );

   const handleNextPage = () => {
      console.log('Next');
   };
   const handlePrevPage = () => {
      console.log('Prev');
   };

   useOutsideClick({
      ref: refs.wrapper,
      handler: () => {
         if (state.searchTerm === '') setState((prev) => ({ ...prev, expanded: false }));
      },
   });

   const handleDownloadExcel = useCallback(() => {
      if (!salesReport || salesReport.length === 0) return;

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet([]);

      // Formatting helpers
      const formatNumber = (value) => {
         const num = Number(value);
         return isNaN(num) ? '0' : num.toLocaleString('en-IN');
      };

      const formatCurrency = (value) => {
         const num = Number(value || 0);
         return `₹${num.toLocaleString('en-IN')}`;
      };

      const formatDate = (dateString) => {
         if (!dateString) return '';
         const date = new Date(dateString);
         return date.toLocaleDateString('en-IN');
      };

      // Add title and date range
      const title = 'Sales Summary Report';
      const dateRange =
         dateRange.fromDate && dateRange.toDate
            ? `From ${formatDate(dateRange.fromDate)} to ${formatDate(dateRange.toDate)}`
            : 'All Dates';

      XLSX.utils.sheet_add_aoa(ws, [[title]], { origin: 'A1' });
      XLSX.utils.sheet_add_aoa(ws, [[dateRange]], { origin: 'A2' });

      // Style title and date range
      ws['A1'].s = {
         font: { sz: 16, bold: true, color: { rgb: '000000' } },
         alignment: { horizontal: 'left' },
      };
      ws['A2'].s = {
         font: { sz: 12, color: { rgb: '000000' } },
         alignment: { horizontal: 'left' },
      };

      // Get visible columns
      const visibleColumns = columnsReportsSalesDetail.filter((col) => state.columnVisibility[col.id] !== false);
      const columnIds = visibleColumns.map((col) => col.id);

      // Start data from row 4
      let rowIndex = 3;

      if (state.reportType === REPORT_TYPE.party) {
         // Party-wise report format
         salesReport.forEach((companyData) => {
            // Company header
            XLSX.utils.sheet_add_aoa(ws, [[companyData.companyName]], {
               origin: XLSX.utils.encode_cell({ r: rowIndex, c: 0 }),
            });
            ws[XLSX.utils.encode_cell({ r: rowIndex, c: 0 })].s = {
               font: { sz: 14, bold: true, color: { rgb: '000000' } },
               fill: { fgColor: { rgb: 'F2F7F2' } },
            };

            // Merge company header across all columns
            ws['!merges'] = [
               ...(ws['!merges'] || []),
               XLSX.utils.decode_range(
                  `${XLSX.utils.encode_cell({ r: rowIndex, c: 0 })}:${XLSX.utils.encode_cell({
                     r: rowIndex,
                     c: visibleColumns.length - 1,
                  })}`,
               ),
            ];
            rowIndex++;

            // Calculate company totals
            const companyTotals = companyData.data.reduce(
               (acc, account) => {
                  acc.invoices += account.data?.length || 0;
                  acc.qty1 += Number(account.totalQty1) || 0;
                  acc.qty2 += Number(account.totalQty2) || 0;
                  acc.qty3 += Number(account.totalQty3) || 0;
                  acc.netAmount += Number(account.totalNetAmt) || 0;
                  return acc;
               },
               { invoices: 0, qty1: 0, qty2: 0, qty3: 0, netAmount: 0 },
            );

            // Add company summary
            const summaryRow = [
               'Company Summary',
               `Invoices: ${companyTotals.invoices}`,
               `Qty 1: ${formatNumber(companyTotals.qty1)}`,
               `Qty 2: ${formatNumber(companyTotals.qty2)}`,
               `Qty 3: ${formatNumber(companyTotals.qty3)}`,
               `Net Amount: ${formatCurrency(companyTotals.netAmount)}`,
            ];
            XLSX.utils.sheet_add_aoa(ws, [summaryRow], { origin: XLSX.utils.encode_cell({ r: rowIndex, c: 0 }) });

            // Style summary row
            summaryRow.forEach((_, colIndex) => {
               ws[XLSX.utils.encode_cell({ r: rowIndex, c: colIndex })].s = {
                  font: { bold: true, color: { rgb: '000000' } },
               };
            });
            rowIndex++;

            // Add account data
            companyData.data.forEach((accountData) => {
               // Account summary row
               const accountRow = [
                  accountData.accountName,
                  accountData.data?.length || 0,
                  formatNumber(accountData.totalQty1),
                  formatNumber(accountData.totalQty2),
                  formatNumber(accountData.totalQty3),
                  formatCurrency(accountData.totalNetAmt),
               ];
               XLSX.utils.sheet_add_aoa(ws, [accountRow], { origin: XLSX.utils.encode_cell({ r: rowIndex, c: 0 }) });

               // Style account row
               accountRow.forEach((_, colIndex) => {
                  ws[XLSX.utils.encode_cell({ r: rowIndex, c: colIndex })].s = {
                     font: { color: { rgb: '000000' } },
                     fill: { fgColor: { rgb: colIndex === 0 ? 'E6E6E6' : 'F5F5F5' } },
                     alignment: { horizontal: colIndex === 0 ? 'left' : 'right' },
                  };
               });
               rowIndex++;
            });

            // Add space between companies
            rowIndex += 2;
         });
      } else {
         // Date-wise report format
         const headers = visibleColumns.map((col) => col.header);
         XLSX.utils.sheet_add_aoa(ws, [headers], { origin: XLSX.utils.encode_cell({ r: rowIndex, c: 0 }) });

         // Style headers
         headers.forEach((_, colIndex) => {
            ws[XLSX.utils.encode_cell({ r: rowIndex, c: colIndex })].s = {
               font: { bold: true, color: { rgb: 'FFFFFF' } },
               fill: { fgColor: { rgb: '4F81BD' } },
               alignment: { horizontal: 'center' },
            };
         });
         rowIndex++;

         // Add transaction data
         salesReport.forEach((dateItem) => {
            // Date header
            XLSX.utils.sheet_add_aoa(ws, [[dateItem.companyName]], {
               origin: XLSX.utils.encode_cell({ r: rowIndex, c: 0 }),
            });
            ws[XLSX.utils.encode_cell({ r: rowIndex, c: 0 })].s = {
               font: { sz: 14, bold: true, color: { rgb: '000000' } },
               fill: { fgColor: { rgb: 'F2F7F2' } },
            };

            // Merge date header across all columns
            ws['!merges'] = [
               ...(ws['!merges'] || []),
               XLSX.utils.decode_range(
                  `${XLSX.utils.encode_cell({ r: rowIndex, c: 0 })}:${XLSX.utils.encode_cell({
                     r: rowIndex,
                     c: visibleColumns.length - 1,
                  })}`,
               ),
            ];
            rowIndex++;

            // Add transactions for this date
            dateItem.data?.forEach((item) => {
               item.data?.forEach((transaction) => {
                  const transactionRow = columnIds.map((colId) => {
                     const value = transaction[colId];
                     if (['netAmount', 'basicAmount', 'taxableAmount', 'gstAmount'].includes(colId)) {
                        return formatCurrency(value);
                     } else if (colId === 'date') {
                        return formatDate(value);
                     } else if (colId === 'itemName' && transaction.saleLineItems) {
                        return transaction.saleLineItems.map((item) => item.itemName).join(', ');
                     }
                     return value?.toString() || '';
                  });

                  XLSX.utils.sheet_add_aoa(ws, [transactionRow], {
                     origin: XLSX.utils.encode_cell({ r: rowIndex, c: 0 }),
                  });

                  // Style transaction row
                  transactionRow.forEach((_, colIndex) => {
                     ws[XLSX.utils.encode_cell({ r: rowIndex, c: colIndex })].s = {
                        font: { color: { rgb: '000000' } },
                        fill: { fgColor: { rgb: rowIndex % 2 === 0 ? 'FFFFFF' : 'F5F5F5' } },
                        alignment: {
                           horizontal: [
                              'netAmount',
                              'basicAmount',
                              'taxableAmount',
                              'gstAmount',
                              'qty1',
                              'qty2',
                              'qty3',
                           ].includes(columnIds[colIndex])
                              ? 'right'
                              : 'left',
                        },
                     };
                  });
                  rowIndex++;
               });
            });

            // Add totals row for this date
            const totalsRow = columnIds.map((colId, colIndex) => {
               if (colIndex === 0) return 'Total';
               if (['qty1', 'qty2', 'qty3'].includes(colId)) {
                  const total =
                     dateItem.data?.reduce((sum, item) => {
                        return sum + item.data?.reduce((itemSum, t) => itemSum + (Number(t[colId]) || 0), 0);
                     }, 0) || 0;
                  return formatNumber(total);
               }
               if (['netAmount', 'basicAmount', 'taxableAmount', 'gstAmount'].includes(colId)) {
                  const total =
                     dateItem.data?.reduce((sum, item) => {
                        return sum + item.data?.reduce((itemSum, t) => itemSum + (Number(t[colId]) || 0), 0);
                     }, 0) || 0;
                  return formatCurrency(total);
               }
               return '';
            });

            XLSX.utils.sheet_add_aoa(ws, [totalsRow], { origin: XLSX.utils.encode_cell({ r: rowIndex, c: 0 }) });

            // Style totals row
            totalsRow.forEach((_, colIndex) => {
               ws[XLSX.utils.encode_cell({ r: rowIndex, c: colIndex })].s = {
                  font: { bold: true, color: { rgb: '000000' } },
                  fill: { fgColor: { rgb: 'E6E6E6' } },
                  alignment: {
                     horizontal: [
                        'netAmount',
                        'basicAmount',
                        'taxableAmount',
                        'gstAmount',
                        'qty1',
                        'qty2',
                        'qty3',
                     ].includes(columnIds[colIndex])
                        ? 'right'
                        : 'left',
                  },
               };
            });
            rowIndex += 2;
         });
      }

      // Set column widths
      ws['!cols'] = visibleColumns.map((col) => {
         if (col.id === 'accountName' || col.id === 'itemName') return { wch: 40 };
         if (['netAmount', 'basicAmount', 'taxableAmount', 'gstAmount'].includes(col.id)) return { wch: 20 };
         if (['qty1', 'qty2', 'qty3'].includes(col.id)) return { wch: 15 };
         return { wch: 25 };
      });

      // Add worksheet to workbook
      XLSX.utils.book_append_sheet(wb, ws, 'Sales Summary');

      // Generate file name
      const dateRangeStr =
         dateRange.fromDate && dateRange.toDate
            ? `${new Date(dateRange.fromDate).toLocaleDateString('en-IN').replace(/\//g, '-')}_to_${new Date(
               dateRange.toDate,
            )
               .toLocaleDateString('en-IN')
               .replace(/\//g, '-')}`
            : 'All_Dates';

      // Save the Excel file
      XLSX.writeFile(wb, `Sales_Summary_${dateRangeStr}.xlsx`);
   }, [salesReport, dateRange, state.reportType, state.columnVisibility, state.expandedItems]);

   const handleDownloadPDF = useCallback(() => {
      if (!salesReport || salesReport.length === 0) return;

      const doc = new jsPDF({
         orientation: 'landscape',
         unit: 'mm',
      });

      // Add title and date range
      const title = 'Sales Summary Report';
      const dateRange =
         dateRange.fromDate && dateRange.toDate
            ? `From ${new Date(dateRange.fromDate).toLocaleDateString('en-IN')} to ${new Date(
               dateRange.toDate,
            ).toLocaleDateString('en-IN')}`
            : 'All Dates';

      doc.setFontSize(16);
      doc.setFont('helvetica', 'bold');
      doc.text(title, 14, 10);
      doc.setFontSize(10);
      doc.text(dateRange, 14, 16);

      // Helper functions
      const formatNumber = (value) => {
         const num = Number(value);
         return isNaN(num) ? '0' : num.toLocaleString('en-IN');
      };

      const formatCurrency = (value) => {
         const num = Number(value || 0);
         return `${num.toLocaleString('en-IN')}`;
      };

      const formatDate = (dateString) => {
         if (!dateString) return '';
         const date = new Date(dateString);
         return date.toLocaleDateString('en-IN');
      };

      // Start Y position for tables
      let startY = 25;

      if (state.reportType === REPORT_TYPE.party) {
         // Party-wise report format
         salesReport.forEach((companyData, companyIndex) => {
            // Check if we need a new page
            if (startY > doc.internal.pageSize.height - 60) {
               doc.addPage();
               startY = 20;
            }

            // Calculate company totals
            const companyTotals = companyData.data?.reduce(
               (acc, account) => {
                  acc.invoices += account.data?.length || 0;
                  acc.qty1 += Number(account.totalQty1) || 0;
                  acc.qty2 += Number(account.totalQty2) || 0;
                  acc.qty3 += Number(account.totalQty3) || 0;
                  acc.netAmount += Number(account.totalNetAmt) || 0;
                  return acc;
               },
               { invoices: 0, qty1: 0, qty2: 0, qty3: 0, netAmount: 0 },
            );

            // Company Header
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text(companyData.companyName || 'Unnamed Company', 14, startY);
            startY += 7;

            // Company Summary
            doc.setFontSize(10);
            doc.setFont('helvetica', 'normal');
            doc.text(`Invoices: ${companyTotals.invoices}`, 14, startY);
            doc.text(`Qty 1: ${formatNumber(companyTotals.qty1)}`, 50, startY);
            doc.text(`Qty 2: ${formatNumber(companyTotals.qty2)}`, 80, startY);
            doc.text(`Qty 3: ${formatNumber(companyTotals.qty3)}`, 110, startY);
            doc.text(`Net Amount: ${formatCurrency(companyTotals.netAmount)}`, 140, startY);
            startY += 8;

            // Account Summary Table
            if (companyData.data?.length) {
               const accountHeaders = ['Party Name', 'Invoices', 'Qty 1', 'Qty 2', 'Qty 3', 'Net Amount'];

               const accountRows = companyData.data.map((account) => [
                  account.accountName || 'Unnamed Account',
                  (account.data?.length || 0).toString(),
                  formatNumber(account.totalQty1),
                  formatNumber(account.totalQty2),
                  formatNumber(account.totalQty3),
                  formatCurrency(account.totalNetAmt),
               ]);

               autoTable(doc, {
                  startY,
                  head: [accountHeaders],
                  body: accountRows,
                  headStyles: {
                     fillColor: [41, 128, 185],
                     textColor: [255, 255, 255],
                     fontStyle: 'bold',
                     fontSize: 9,
                  },
                  bodyStyles: {
                     fontSize: 8,
                     textColor: [0, 0, 0],
                  },
                  alternateRowStyles: {
                     fillColor: [245, 245, 245],
                  },
                  columnStyles: {
                     0: { cellWidth: 60 },
                     1: { halign: 'right', cellWidth: 20 },
                     2: { halign: 'right', cellWidth: 20 },
                     3: { halign: 'right', cellWidth: 20 },
                     4: { halign: 'right', cellWidth: 20 },
                     5: { halign: 'right', cellWidth: 30 },
                  },
                  margin: { left: 14 },
               });

               startY = doc.lastAutoTable.finalY + 5;
            }

            // Add spacing between companies
            if (companyIndex < salesReport.length - 1) {
               startY += 10;
            }
         });
      } else {
         // Date-wise report format
         const visibleColumns = columnsReportsSalesDetail.filter((col) => state.columnVisibility[col.id] !== false);
         const columnIds = visibleColumns.map((col) => col.id);

         salesReport.forEach((dateItem, dateIndex) => {
            // Check if we need a new page
            if (startY > doc.internal.pageSize.height - 60) {
               doc.addPage();
               startY = 20;
            }

            // Date/Company Header
            doc.setFontSize(12);
            doc.setFont('helvetica', 'bold');
            doc.text(dateItem.companyName || 'Unnamed Company', 14, startY);
            startY += 7;

            // Prepare transaction data
            const transactions = dateItem.data?.flatMap((item) => item.data || []) || [];

            if (transactions.length > 0) {
               const headers = visibleColumns.map((col) => col.header);

               const transactionRows = transactions.map((transaction) => {
                  return columnIds.map((colId) => {
                     if (['netAmount', 'basicAmount', 'taxableAmount', 'gstAmount'].includes(colId)) {
                        return formatCurrency(transaction[colId]);
                     } else if (colId === 'date') {
                        return formatDate(transaction[colId]);
                     } else if (colId === 'itemName' && transaction.saleLineItems) {
                        return transaction.saleLineItems.map((item) => item.itemName).join(', ');
                     }
                     return transaction[colId]?.toString() || '';
                  });
               });

               autoTable(doc, {
                  startY,
                  head: [headers],
                  body: transactionRows,
                  headStyles: {
                     fillColor: [41, 128, 185],
                     textColor: [255, 255, 255],
                     fontStyle: 'bold',
                     fontSize: 9,
                  },
                  bodyStyles: {
                     fontSize: 8,
                     textColor: [0, 0, 0],
                  },
                  alternateRowStyles: {
                     fillColor: [245, 245, 245],
                  },
                  columnStyles: columnIds.reduce((styles, colId, idx) => {
                     if (
                        ['netAmount', 'basicAmount', 'taxableAmount', 'gstAmount', 'qty1', 'qty2', 'qty3'].includes(
                           colId,
                        )
                     ) {
                        styles[idx] = { halign: 'right' };
                     }
                     return styles;
                  }, {}),
                  margin: { left: 14 },
               });

               startY = doc.lastAutoTable.finalY + 5;

               // Add totals row
               const totalsRow = columnIds.map((colId, idx) => {
                  if (idx === 0) return 'Total';
                  if (['qty1', 'qty2', 'qty3'].includes(colId)) {
                     const total = transactions.reduce((sum, t) => sum + (Number(t[colId]) || 0), 0);
                     return formatNumber(total);
                  }
                  if (['netAmount', 'basicAmount', 'taxableAmount', 'gstAmount'].includes(colId)) {
                     const total = transactions.reduce((sum, t) => sum + (Number(t[colId]) || 0), 0);
                     return formatCurrency(total);
                  }
                  return '';
               });

               autoTable(doc, {
                  startY,
                  body: [totalsRow],
                  styles: {
                     fontStyle: 'bold',
                     fontSize: 9,
                     fillColor: [230, 230, 230],
                  },
                  columnStyles: columnIds.reduce((styles, colId, idx) => {
                     if (
                        ['netAmount', 'basicAmount', 'taxableAmount', 'gstAmount', 'qty1', 'qty2', 'qty3'].includes(
                           colId,
                        )
                     ) {
                        styles[idx] = { halign: 'right' };
                     }
                     return styles;
                  }, {}),
                  margin: { left: 14 },
               });

               startY = doc.lastAutoTable.finalY + 5;
            }

            // Add spacing between date items
            if (dateIndex < salesReport.length - 1) {
               startY += 10;
            }
         });
      }

      // Add footer to all pages
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
         doc.setPage(i);
         doc.setFontSize(10);
         doc.setFont('helvetica', 'normal');
         doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 20, doc.internal.pageSize.height - 10, {
            align: 'right',
         });
         doc.text(`Generated on: ${new Date().toLocaleString('en-IN')}`, 14, doc.internal.pageSize.height - 10);
      }

      // Save the PDF
      const dateRangeStr =
         dateRange.fromDate && dateRange.toDate
            ? `${new Date(dateRange.fromDate).toLocaleDateString('en-IN').replace(/\//g, '-')}_to_${new Date(
               dateRange.toDate,
            )
               .toLocaleDateString('en-IN')
               .replace(/\//g, '-')}`
            : 'All_Dates';

      doc.save(`Sales_Summary_${dateRangeStr}.pdf`);
   }, [salesReport, dateRange, state.reportType, state.columnVisibility, state.expandedItems]);

   const deletePurchaseReturn = () => {
      const selectedDaybook = daybookData.find((db) => db.id === viewDetail?.daybookId);
      console.log('viewDetail data', viewDetail, selectedDaybook);
      if (selectedDaybook && selectedDaybook?.askPasswordOnDelete) {
         setDataId({ id: viewDetail?.id, daybookId: viewDetail.daybookId, accountId: viewDetail.accountId });
         setIsModalOpen(true);
         setIsOpenPurchaseReturnSingleView(false);
      } else {
         deleteRecord(
            viewDetail?.id,
            deletePurchaseInvoice,
            dispatch,
            user?.loginhistory?.companyId,
            userGlobalSettingsData,
         );
         setIsOpenPurchaseReturnSingleView(false);
      }
      // deleteRecord(
      //    viewDetail?.id,
      //    deletePurchaseInvoice,
      //    dispatch,
      //    user?.loginhistory?.companyId,
      //    userGlobalSettingsData,
      // );
      // setIsOpenPurchaseReturnSingleView(false);
   };

   const handleSalesDetailsClose = () => {
      setIsOpenPurchaseReturnSingleView();
   };

   const handleRowClick = useCallback(async (data) => {
      const storedCompanyId = localStorage.getItem('companyId');
      const companyId = parseInt(storedCompanyId);
      if (data.companyId === companyId && (admin || rights?.purchaseinvoice?.view)) {
         await dispatch(getPurchaseInvoice(data.id));
         setIsOpenPurchaseReturnSingleView(true);
      }
   }, [dispatch, user?.loginhistory?.companyId, admin, rights?.purchaseinvoice?.view]);

   useEffect(() => {
      const calculateHeight = () => {
         const windowHeight = window.innerHeight;
         const headerHeight = 250;
         const cardMargin = 15;
         const padding = 32;
         const availableHeight = windowHeight - headerHeight - cardMargin - padding;
         setContainerHeight(Math.max(availableHeight, 400));
      };
      calculateHeight();
      window.addEventListener('resize', calculateHeight);
      return () => window.removeEventListener('resize', calculateHeight);
   }, []);

   const flattenHierarchicalData = useCallback(() => {
      if (!salesReport || salesReport.length === 0) return [];

      const flatItems = [];

      // Add GRAND TOTAL at the TOP if multiple companies are selected
      if (state.companyFilter.length > 1) {
         // Calculate grand totals across all companies
         const grandTotals = {
            totalQty: 0,
            totalNetAmount: 0,
            totalBasicAmount: 0,
            totalTaxableAmount: 0,
            totalGstAmount: 0,
            totalBillAmount: 0,
            totalInvoices: 0,
         };

         salesReport.forEach((companyData) => {
            if (state.reportType === REPORT_TYPE.party) {
               // Party-wise calculation
               companyData.data?.forEach((accountData) => {
                  grandTotals.totalInvoices += accountData.data?.length || 0;
                  grandTotals.totalQty += Number(accountData.totalQty1) || 0;
                  grandTotals.totalNetAmount += Number(accountData.totalNetAmt) || 0;

                  accountData.data?.forEach((transaction) => {
                     grandTotals.totalBasicAmount += Number(transaction.basicAmount) || 0;
                     grandTotals.totalTaxableAmount += Number(transaction.taxableAmount) || 0;
                     grandTotals.totalGstAmount += Number(transaction.gstAmount) || 0;
                     grandTotals.totalBillAmount += Number(transaction.netAmount) || 0;
                  });
               });
            } else {
               // Date-wise calculation
               companyData.data?.forEach((item) => {
                  item.data?.forEach((transaction) => {
                     grandTotals.totalInvoices++;
                     grandTotals.totalQty += Number(transaction.totalQty1 || transaction.qty || 0);
                     grandTotals.totalNetAmount += Number(transaction.netAmount) || 0;
                     grandTotals.totalBasicAmount += Number(transaction.basicAmount) || 0;
                     grandTotals.totalTaxableAmount += Number(transaction.taxableAmount) || 0;
                     grandTotals.totalGstAmount += Number(transaction.gstAmount) || 0;
                     grandTotals.totalBillAmount += Number(transaction.billAmount || transaction.netAmount) || 0;
                  });
               });
            }
         });

         // Add grand total row at the beginning
         flatItems.push({
            type: 'grand-total',
            data: grandTotals,
            id: 'grand-total-top',
            companyCount: state.companyFilter.length,
         });
      }

      if (state.reportType === REPORT_TYPE.party) {
         salesReport.forEach((companyData, companyIndex) => {
            const companyKey = `company-${companyData.companyId || companyIndex}`;

            flatItems.push({
               type: 'company-header',
               data: companyData,
               id: companyKey,
               companyId: companyData.companyId,
            });

            if (state.expandedItems[companyKey] && companyData.data) {
               companyData.data.forEach((accountData, accountIndex) => {
                  const accountKey = `account-${accountData.accountId || accountIndex}`;

                  flatItems.push({
                     type: 'account-header',
                     data: accountData,
                     id: accountKey,
                     companyId: companyData.companyId,
                     accountId: accountData.accountId,
                  });

                  if (state.expandedItems[accountKey] && accountData.data) {
                     accountData.data.forEach((transaction, transIndex) => {
                        flatItems.push({
                           type: 'transaction-row',
                           data: transaction,
                           id: `trans-${transaction.id || transIndex}`,
                           companyId: companyData.companyId,
                        });
                     });
                  }
               });
            }
         });
      } else {
         // Date-wise format
         salesReport.forEach((dateItem, dateIndex) => {
            flatItems.push({
               type: 'date-header',
               data: dateItem,
               id: `date-${dateItem.date || dateIndex}`,
               companyId: dateItem.companyId,
            });

            dateItem.data?.forEach((item) => {
               item.data?.forEach((transaction, transIndex) => {
                  flatItems.push({
                     type: 'transaction-row',
                     data: transaction,
                     id: `trans-${transaction.id || transIndex}`,
                     companyId: dateItem.companyId,
                  });
               });
            });

            flatItems.push({
               type: 'total-row',
               data: dateItem,
               id: `total-${dateItem.date || dateIndex}`,
            });
         });
      }

      return flatItems;
   }, [salesReport, state.reportType, state.expandedItems, state.companyFilter]);

   useEffect(() => {
      if (salesReport.length > 0) {
         const flat = flattenHierarchicalData();
         setFlattenedData(flat);
         if (listRef.current) {
            listRef.current.resetAfterIndex(0);
         }
      } else {
         setFlattenedData([]);
      }
   }, [salesReport, flattenHierarchicalData]);

   useEffect(() => {
      if (listRef.current) {
         listRef.current.resetAfterIndex(0);
      }
   }, [state.expandedItems]);

   const getItemSize = useCallback(
      (index) => {
         const item = flattenedData[index];
         if (!item) return 48;
         switch (item.type) {
            case 'grand-total': return 45; // Taller row for grand total
            case 'company-header': return 35;
            case 'account-header': return 30;
            case 'date-header': return 35;
            case 'transaction-row': return 30;
            default: return 30;
         }
      },
      [flattenedData],
   );
   const formatCurrencyWithSmallDecimals = useCallback((value) => {
      if (value == null || value === '') return '₹0';
      const numValue = Number(value);
      if (isNaN(numValue)) return '₹0';

      const formatted = numValue.toLocaleString('en-IN', {
         minimumFractionDigits: 2,
         maximumFractionDigits: 2,
      });

      const parts = formatted.toString().split('.');

      if (parts.length === 2) {
         return (
            <>
               ₹{parts[0]}.<span style={{ fontSize: '0.7em' }}>{parts[1]}</span>
            </>
         );
      }

      return `₹${formatted}`;
   }, []);

   const RowRenderer = useCallback(
      ({ index, style }) => {
         const item = flattenedData[index];
         if (!item) return <div style={style} />;

         switch (item.type) {

            case 'grand-total': {
               const totals = item.data;
               const companyCount = item.companyCount;

               return (
                  <Grid
                     style={style}
                     templateColumns={`repeat(${table.getVisibleFlatColumns().length}, minmax(100px, 1fr))`}
                     bg={isDark ? '#1a3a6b' : '#E8E2D9'}
                     borderTop="3px solid"
                     borderBottom="2px solid"
                     borderColor="green.400"
                     fontWeight="bold"
                     fontSize="14px"
                     position="sticky"
                     top="0"
                     zIndex="11"
                     alignItems="center">
                     {table.getVisibleFlatColumns().map((column, colIndex, visibleCols) => {
                        // First column - Grand Total label
                        if (colIndex === 0) {
                           return (
                              <GridItem
                                 key={column.id}
                                 p="12px"
                                 color={isDark ? 'white' : 'black'}>
                                 GRAND TOTAL ({companyCount} Companies)
                              </GridItem>
                           );
                        }

                        const columnId = column.id;

                        // Handle Quantity columns
                        if (columnId === 'totalQty1' || columnId === 'qty' || columnId.toLowerCase().includes('qty')) {
                           return (
                              <GridItem
                                 key={column.id}
                                 p="12px"
                                 textAlign="right"
                                 color={isDark ? 'green.300' : 'green.700'}>
                                 {totals.totalQty.toLocaleString()}
                              </GridItem>
                           );
                        }

                        // Handle Net Amount
                        if (columnId === 'netAmount') {
                           return (
                              <GridItem
                                 key={column.id}
                                 p="12px"
                                 textAlign="right"
                                 color={isDark ? 'green.300' : 'green.700'}>
                                 ₹{totals.totalNetAmount.toLocaleString()}
                              </GridItem>
                           );
                        }

                        // Handle Bill Amount
                        if (columnId === 'billAmount') {
                           return (
                              <GridItem
                                 key={column.id}
                                 p="12px"
                                 textAlign="right"
                                 color={isDark ? 'green.300' : 'green.700'}>
                                 ₹{totals.totalBillAmount.toLocaleString()}
                              </GridItem>
                           );
                        }

                        // Handle Basic Amount
                        if (columnId === 'basicAmount') {
                           return (
                              <GridItem
                                 key={column.id}
                                 p="12px"
                                 textAlign="right"
                                 color={isDark ? 'green.300' : 'green.700'}>
                                 ₹{totals.totalBasicAmount.toLocaleString()}
                              </GridItem>
                           );
                        }

                        // Handle Taxable Amount
                        if (columnId === 'taxableAmount') {
                           return (
                              <GridItem
                                 key={column.id}
                                 p="12px"
                                 textAlign="right"
                                 color={isDark ? 'green.300' : 'green.700'}>
                                 ₹{totals.totalTaxableAmount.toLocaleString()}
                              </GridItem>
                           );
                        }

                        // Handle GST Amount
                        if (columnId === 'gstAmount') {
                           return (
                              <GridItem
                                 key={column.id}
                                 p="12px"
                                 textAlign="right"
                                 color={isDark ? 'green.300' : 'green.700'}>
                                 ₹{totals.totalGstAmount.toLocaleString()}
                              </GridItem>
                           );
                        }

                        // Empty cells for other columns
                        return <GridItem key={column.id} p="12px" />;
                     })}
                  </Grid>
               );
            }

            case 'company-header': {
               const company = item.data;
               const totalQty = company.data?.reduce(
                  (sum, acc) => sum + (Number(acc.totalQty1) || 0), 0) || 0;
               const totalAmt = company.data?.reduce(
                  (sum, acc) => sum + (Number(acc.totalNetAmt) || 0), 0) || 0;

               // Get all transactions from the company data
               const allTransactions = company.data || [];

               return (
                  <Box
                     style={style}
                     bg={isDark ? 'blue.700' : '#F1F0E8'}
                     p="8px"
                     borderBottom="2px solid"
                     borderColor={theme.borderColor}
                     cursor="pointer"
                     onClick={() => toggleExpand(item.id)}>
                     <Flex justifyContent="space-between" alignItems="center">
                        <Text fontWeight="bold" fontSize="15px"
                           color={isDark ? 'white' : 'black'}>
                           {company.companyName}
                        </Text>
                        <Flex gap={6}>
                           {/* Company totals section - you might want to show totals here */}
                           <Text fontSize="14px" color={isDark ? 'gray.300' : 'gray.600'}>
                              Qty: {totalQty}
                           </Text>
                           <Text fontSize="14px" color={isDark ? 'gray.300' : 'gray.600'}>
                              Amt: ₹{totalAmt.toLocaleString()}
                           </Text>

                           {/* Expand/collapse icon */}
                           {state.expandedItems[item.id]
                              ? <ChevronUpIcon />
                              : <ChevronDownIcon />}
                        </Flex>
                     </Flex>
                  </Box>
               );
            }

            case 'account-header': {
               const account = item.data;
               return (
                  <Box
                     style={style}
                     bg={isDark ? 'blue.900' : '#F5EEE6'}
                     p="6px"
                     pl="20px"
                     borderBottom="1px solid"
                     borderColor={theme.borderColor}
                     cursor="pointer"
                     onClick={() => toggleExpand(item.id)}>
                     <Flex justifyContent="space-between" alignItems="center">
                        <Flex align="center" gap={2}>
                           <Text fontWeight="600" fontSize="13px"
                              color={isDark ? 'white' : 'black'}>
                              {account.accountName}
                           </Text>
                           {state.expandedItems[item.id]
                              ? <ChevronUpIcon />
                              : <ChevronDownIcon />}
                        </Flex>
                        <Flex gap={6}>
                           <Text fontSize="13px" color={isDark ? 'white' : 'black'}>
                              Qty: {account.totalQty1?.toLocaleString() || 0}
                           </Text>
                           <Text fontSize="13px" color={isDark ? 'white' : 'black'}>
                              Amt: ₹{account.totalNetAmt?.toLocaleString() || 0}
                           </Text>
                        </Flex>
                     </Flex>
                  </Box>
               );
            }

            case 'date-header': {
               const dateItem = item.data;
               return (
                  <Box
                     style={style}
                     bg={isDark ? 'blue.700' : 'blue.50'}
                     p="8px"
                     borderBottom="2px solid"
                     borderColor={theme.borderColor}>
                     <Text fontWeight="bold" fontSize="14px"
                        color={isDark ? 'white' : 'black'}>
                        {dateItem.companyName}
                     </Text>
                  </Box>
               );
            }

            case 'transaction-row': {
               const transaction = item.data;
               const isOddRow = index % 2 !== 0;
               return (
                  <Grid
                     style={style}
                     templateColumns={`repeat(${table.getVisibleFlatColumns().length}, minmax(100px, 1fr))`}
                     cursor="pointer"
                     onClick={() => handleRowClick(transaction)}
                     bg={isOddRow
                        ? (isDark ? 'rgba(2,14,56,0.7)' : 'rgba(235,244,255,0.5)')
                        : 'transparent'}
                     _hover={{ bg: 'rgba(0,63,140,0.08)' }}
                     borderBottom="1px solid"
                     borderColor={theme.borderColor}
                     fontSize="13px"
                     alignItems="center">
                     {table.getVisibleFlatColumns().map((column) => {
                        const columnId = column.id;
                        const cellValue = transaction[columnId];
                        const isNumeric = [
                           'netAmount', 'dueDays', 'returnadjamount', 'otheradjamount',
                           'balanceamount', 'recDate', 'recBook', 'billAmount',
                           'taxableAmount', 'gstAmount', 'totalQty1', 'itemRate', 'basicAmount'
                        ].includes(columnId);

                        let content = cellValue || '-';

                        if (columnId === 'itemName' && transaction.saleLineItems) {
                           content = transaction.saleLineItems.map(i => i.itemName).join(', ');
                        } else if (columnId.includes('Date') && cellValue) {
                           content = new Date(cellValue).toLocaleDateString();
                        } else if (['netAmount', 'basicAmount', 'taxableAmount',
                           'gstAmount', 'billAmount'].includes(columnId)) {
                           content = formatCurrencyWithSmallDecimals(cellValue);
                        }

                        return (
                           <GridItem
                              key={columnId}
                              p="10px"
                              textAlign={isNumeric ? 'right' : 'left'}
                              color={isDark ? 'white' : 'black'}
                              whiteSpace="nowrap"
                              overflow="hidden"
                              textOverflow="ellipsis">
                              {content}
                           </GridItem>
                        );
                     })}
                  </Grid>
               );
            }

            default:
               return <div style={style} />;
         }
      },
      [
         flattenedData, isDark, theme, table, state.expandedItems,
         toggleExpand, handleRowClick, formatCurrencyWithSmallDecimals,
      ],
   );

   const renderTable = () => (
      <Grid overflow="auto">
         <Card mt="15px" borderRadius="7px">
            {isFilterLoading && (
               <Box
                  position="fixed"
                  top={0} left={0} right={0} bottom={0}
                  zIndex={9999}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bg={isDark ? 'rgba(10,20,50,0.60)' : 'rgba(255,255,255,0.60)'}
                  backdropFilter="blur(3px)"
               >
                  <Flex direction="column" align="center" gap={3}>
                     <Loader />
                  </Flex>
               </Box>
            )}
            <Box
               ref={containerRef}
               overflowX="auto"
               className="custom-scrollbar"
               position="relative"
               width="100%"
               height={`${containerHeight}px`}>

               {/* STICKY HEADER - outside VariableSizeList */}
               <Grid
                  templateColumns={`repeat(${table.getVisibleFlatColumns().length}, minmax(100px, 1fr))`}
                  bg={isDark ? 'gray.800' : 'gray.50'}
                  borderBottom="2px solid"
                  borderColor={isDark ? 'indigo.500' : 'indigo.300'}
                  position="sticky"
                  top="0"
                  zIndex="10"
                  fontWeight="700"
                  fontSize="12px">
                  {table.getHeaderGroups().map((headerGroup) =>
                     headerGroup.headers.map((header) => {
                        if (!header.column.getIsVisible()) return null;
                        const columnId = header.column.id;
                        const isNumeric = [
                           'netAmount', 'dueDays', 'returnadjamount', 'otheradjamount',
                           'balanceamount', 'recDate', 'recBook', 'billAmount',
                           'taxableAmount', 'gstAmount', 'totalQty1', 'itemRate', 'basicAmount'
                        ].includes(columnId);

                        return (
                           <GridItem
                              key={header.id}
                              p="10px"
                              cursor="pointer"
                              textAlign={isNumeric ? 'right' : 'left'}
                              color={isDark ? 'gray.100' : 'gray.700'}
                              onClick={header.column.getToggleSortingHandler()}>
                              <Flex
                                 justifyContent={isNumeric ? 'flex-end' : 'flex-start'}
                                 align="center"
                                 gap={2}>
                                 {flexRender(
                                    header.column.columnDef.header,
                                    header.getContext()
                                 )}
                                 {{
                                    asc: <ChevronUpIcon />,
                                    desc: <ChevronDownIcon />,
                                 }[header.column.getIsSorted()] ?? null}
                              </Flex>
                           </GridItem>
                        );
                     })
                  )}
               </Grid>

               {/* BODY - VariableSizeList */}
               {state.isLoading ? (
                  <Flex justifyContent="center" alignItems="center" height="100%">
                     <Spinner size="xl" />
                  </Flex>
               ) : flattenedData.length === 0 ? (
                  <Flex direction="column" align="center" justify="center" height="100%">
                     <img src={nodata} alt="No Data Found" width="200px" height="200px" />
                     <Text mt={4}>No purchase data found</Text>
                  </Flex>
               ) : (
                  <VariableSizeList
                     ref={listRef}
                     height={containerHeight}
                     width="100%"
                     itemCount={flattenedData.length}
                     itemSize={getItemSize}
                     overscanCount={15}
                     estimatedItemSize={48}>
                     {RowRenderer}
                  </VariableSizeList>
               )}
            </Box>
         </Card>
      </Grid>
   );

   const renderHeader = () => (
      <Flex pr="25px" mb="8px" justifyContent="space-between" align="center" mt="50px" overflow="hidden">
         <Flex>
            <Image
               src={back}
               alt="Go Back"
               width="15px"
               onClick={() => window.history.back()}
               style={{ cursor: 'pointer' }}
            />
            <Text color={theme.textColor} fontSize="15px" fontWeight="700" lineHeight="100%" ml={5}>
               Purchase Summary
            </Text>
         </Flex>
      </Flex>
   );

   const renderFilters = () => (
      <Box mt="15px" bg={theme.backgroundColor} px={4} py={0} borderRadius="7px">
         <Flex alignItems="center" gap={3} justifyContent="end">
            <Box my={2} width="300px" fontSize="13px" fontWeight={500} display={{ base: 'none', xl: 'block' }}>
               <GridItem className="seperator-width" mt={5}>
                  {renderDynamicDropdown(
                     'company',
                     'Company',
                     lastcompanyOptions,
                     { value: state.companyFilter, onChange: handleCompanyChange },
                     {},
                     theme.textColor,
                     false,
                     'Select company',
                     true,
                     undefined,
                     true,
                  )}
               </GridItem>
            </Box>

            <Box flex="1" display={{ base: 'none', xl: 'flex' }}>
               <Flex alignItems="center" mt={3}>
                  <PeriodDatePicker
                     value={[new Date(dateRange.fromDate), new Date(dateRange.toDate)]}
                     onChange={handleDateRangeChange}
                     defaultPeriod="last7Days"
                     style={{ width: 250, marginRight: 20, borderRadius: 5, height: 35 }}
                  />
               </Flex>

               <Box my={2} fontSize={'13px'} fontWeight={500} zIndex={15}>
                  <label>Report Type</label>
                  <Select
                     options={REPORT_TYPE_OPTIONS}
                     value={REPORT_TYPE_OPTIONS.find((option) => option.value === state.reportType)}
                     onChange={(selectedOption) => {
                        setState((prev) => ({
                           ...prev,
                           reportType: selectedOption.value,
                           expandedItems: {},
                        }));
                     }}
                     placeholder="Select Report Type"
                     styles={{
                        control: (baseStyles, state) => ({
                           ...baseStyles,
                           minHeight: '30px',
                           height: '30px',
                           backgroundColor: controlBg,
                           borderColor: controlBorder,
                           boxShadow: state.isFocused ? focusBoxShadow : baseStyles.boxShadow,
                           '&:hover': {
                              borderColor: controlBorderHover,
                           },
                        }),
                        valueContainer: (baseStyles) => ({
                           ...baseStyles,
                           height: '30px',
                           padding: '0 6px',
                           color: theme.textColor,
                        }),
                        indicatorsContainer: (baseStyles) => ({
                           ...baseStyles,
                           height: '30px',
                        }),
                        singleValue: (baseStyles) => ({
                           ...baseStyles,
                           color: theme.textColor,
                        }),
                        placeholder: (baseStyles) => ({
                           ...baseStyles,
                           color: placeholderColor,
                        }),
                        option: (baseStyles, state) => ({
                           ...baseStyles,
                           backgroundColor: state.isFocused ? optionHoverBg : optionBg,
                           color: theme.textColor,
                           '&:hover': {
                              backgroundColor: optionHoverBg,
                           },
                        }),
                        menu: (baseStyles) => ({
                           ...baseStyles,
                           backgroundColor: menuBg,
                           border: `1px solid ${menuBorder}`,
                        }),
                        dropdownIndicator: (baseStyles) => ({
                           ...baseStyles,
                           color: indicatorColor,
                           '&:hover': {
                              color: indicatorHoverColor,
                           },
                        }),
                        clearIndicator: (baseStyles) => ({
                           ...baseStyles,
                           color: indicatorColor,
                           '&:hover': {
                              color: indicatorHoverColor,
                           },
                        }),
                     }}
                  />
               </Box>
            </Box>

            <Flex alignItems="center">
               <Box flex="1" mr={3}>
                  <MotionFlex
                     ref={refs.wrapper}
                     align="center"
                     bg={theme.bg}
                     borderRadius="full"
                     cursor="pointer"
                     px={state.expanded ? 3 : 0}
                     w={state.expanded ? '250px' : '40px'}
                     h="40px"
                     transition="all 0.3s ease"
                     onClick={() => setState((prev) => ({ ...prev, expanded: true }))}>
                     {state.isLoading ? (
                        <Spinner size="sm" color={theme.icon} />
                     ) : (
                        <MotionSearchIcon
                           color={theme.icon}
                           animate={{ rotate: state.expanded ? 0 : 90 }}
                           transition={{ duration: 0.3 }}
                           style={{
                              marginRight: state.expanded ? '8px' : '10px',
                              marginLeft: state.expanded ? '0' : '10px',
                           }}
                        />
                     )}
                     {state.expanded && (
                        <InputGroup width="200px">
                           <Input
                              variant="unstyled"
                              placeholder="Search ..."
                              value={state.searchTerm}
                              onChange={handleSearchChange}
                              autoFocus
                              color={theme.icon}
                              flex="1"
                           />
                        </InputGroup>
                     )}
                  </MotionFlex>
               </Box>

               <Button
                  onClick={() => {
                     console.log('onClick work');
                     fetchData();
                  }}
                  _hover={{ bg: 'blue.50' }}
                  mr={2}>
                  <LuRefreshCcw size={20} mr={2} />
               </Button>

               <Button cursor={'pointer'} onClick={onFilterOpen} mr={2}>
                  <IoFilterOutline size={22} />
               </Button>

               <Tooltip label="Show/Hide Columns">
                  <IconButton
                     icon={<ViewIcon />}
                     onClick={() => setState((prev) => ({ ...prev, isColumnSelectorOpen: true }))}
                     aria-label="Column Visibility"
                  />
               </Tooltip>

               <Flex mr={2}>
                  <Popover>
                     <PopoverTrigger>
                        <IconButton icon={<Image src={Download} boxSize="24px" />} aria-label="Download Options" />
                     </PopoverTrigger>
                     <PopoverContent width="220px" zIndex="10" bg={theme.backgroundColor} mr={8}>
                        <PopoverBody p="">
                           <Button
                              width="100%"
                              variant="ghost"
                              _hover={{ bg: 'blue.100' }}
                              onClick={handleDownloadExcel}
                              justifyContent="space-between"
                              mb={3}>
                              <img src={xls} alt="Download XLS" width="30px" />
                              Download Excel
                           </Button>
                           <Button
                              width="100%"
                              variant="ghost"
                              _hover={{ bg: 'blue.100' }}
                              onClick={handleDownloadPDF}
                              justifyContent="space-between"
                              mb={3}>
                              <img src={xls} alt="Download PDF" width="30px" />
                              Download Pdf
                           </Button>
                        </PopoverBody>
                     </PopoverContent>
                  </Popover>
               </Flex>
            </Flex>

            <Box ml={1}>
               <Flex display={{ base: 'block', xl: 'none' }}>
                  <Button variant="unstyled" onClick={onOpen}>
                     <Image src={DotIcon} alt="Menu" width="25px" />
                  </Button>
               </Flex>
            </Box>
         </Flex>
      </Box>
   );

   const renderMobileDrawer = () => (
      <Drawer isOpen={isOpen} placement="bottom" onClose={onClose} finalFocusRef={refs.fromDateInput}>
         <DrawerOverlay />
         <DrawerContent borderTopRadius="20px">
            <DrawerCloseButton />
            <DrawerHeader borderBottomWidth="1px">Filters</DrawerHeader>
            <DrawerBody>
               <GridItem className="seperator-width">
                  {renderDynamicDropdown(
                     'company',
                     'Company',
                     lastcompanyOptions,
                     { value: state.companyFilter, onChange: handleCompanyChange },
                     {},
                     theme.textColor,
                     false,
                     'Select company',
                     true,
                     undefined,
                     true,
                  )}
               </GridItem>
               <Box my={2} fontSize="13px" fontWeight={500}>
                  <label>Report Type</label>
                  <Select
                     options={REPORT_TYPE_OPTIONS}
                     value={REPORT_TYPE_OPTIONS.find((option) => option.value === state.reportType)}
                     onChange={(selectedOption) => {
                        setState((prev) => ({
                           ...prev,
                           reportType: selectedOption.value,
                           expandedItems: {},
                        }));
                     }}
                     placeholder="Select Report Type"
                  />
               </Box>
               <Box alignItems="center" mt={3}>
                  <PeriodDatePicker
                     value={[new Date(dateRange.fromDate), new Date(dateRange.toDate)]}
                     onChange={handleDateRangeChange}
                     defaultPeriod="last7Days"
                     style={{ width: 250, marginRight: 20, borderRadius: 5, height: 35 }}
                  />
               </Box>

               <Button colorScheme="green" width="100%" onClick={onClose} mt={4}>
                  Apply Filters
               </Button>
            </DrawerBody>
         </DrawerContent>
      </Drawer>
   );

   return (
      <>
         {renderHeader()}
         {renderFilters()}
         {renderTable()}
         {renderMobileDrawer()}
         <ColumnVisibilityModal
            isOpen={state.isColumnSelectorOpen}
            onClose={() => setState((prev) => ({ ...prev, isColumnSelectorOpen: false }))}
            columns={columnsReportsSalesDetail}
            columnVisibility={state.columnVisibility}
            setColumnVisibility={(visibility) => setState((prev) => ({ ...prev, columnVisibility: visibility }))}
         />

         <SalesPurchaseFilter
            type={state.reportType}
            isOpen={isFilterOpen}
            onClose={onFilterClose}
            companyIds={companyIds}
            fromDate={dateRange.fromDate}
            toDate={dateRange.toDate}
            entityType="purchaseDetail"
            onApply={() => setIsFilterLoading(true)}
         />

         <SalesDetails
            isOpen={isOpenPurchaseReturnSingleView}
            onClose={handleSalesDetailsClose}
            singleEntityRights={purchaseInvoiceRight || purchaseReturnRight}
            title={'Purchase Invoice'}
            entityType={'purchaseinvoice'}
            singleEntity={viewDetail}
            handlePrevPage={handlePrevPage}
            handleNextPage={handleNextPage}
            setIsEditFormOpen={setIsEditFormOpen}
            setIsOpenSingleView={setIsOpenPurchaseReturnSingleView}
            isEditFormOpen={isEditFormOpen}
            // paginate={paginateData}
            viewDetail={viewDetail}
            lineItems={viewDetail?.lineitems}
            billTerms={viewDetail?.purchasereturnbillteams}
            onDelete={deletePurchaseReturn}
            handleSerchClose={() => console.log('')}
         />

         <DeletePasswordModal
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onConfirm={deletePurchaseReturn}
            id={dataId}
            deleteApi={deletePurchaseInvoice}
            dispatch={dispatch}
            type="purchase"
            userGlobalSettingsData={userGlobalSettingsData}
         />
      </>
   );
};

export default PurchaseSummary;
